// src/components/layout/Sidebar.tsx

import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { AppConfig } from '../../config/app.config';

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
}

const IconDashboard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
);

const IconPackage = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

const IconLogout = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
    <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <IconDashboard /> },
  { label: 'Paquetes',  to: '/packages',  icon: <IconPackage /> },
];

const ROLE_BADGE: Record<string, { label: string; color: string }> = {
  ADMIN:    { label: 'Admin',     color: '#ef4444' },
  OPERATOR: { label: 'Operador',  color: '#f59e0b' },
  VIEWER:   { label: 'Viewer',    color: '#10b981' },
};

export const Sidebar: React.FC = () => {
  const { user, logout, isAdmin } = useAuthContext();
  const navigate = useNavigate();

  const role     = user?.roles.split(',')[0]?.trim() ?? '';
  const badge    = ROLE_BADGE[role] ?? { label: role, color: '#6b7280' };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <aside className="sidebar">
      {/* Brand */}
      <div className="sidebar__brand">
        <svg className="sidebar__logo" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="12" fill="currentColor" fillOpacity="0.15"/>
          <path d="M10 34L18 14l8 12 6-8 6 16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="38" cy="14" r="3" fill="currentColor"/>
        </svg>
        <span className="sidebar__brand-name">{AppConfig.app.name}</span>
      </div>

      {/* Nav */}
      <nav className="sidebar__nav">
        {NAV_ITEMS.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
            }
          >
            <span className="sidebar__link-icon">{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User info */}
      <div className="sidebar__footer">
        <div className="sidebar__user">
          <div className="sidebar__avatar">{user?.email.charAt(0).toUpperCase()}</div>
          <div className="sidebar__user-info">
            <span className="sidebar__user-email">{user?.email}</span>
            <span
              className="sidebar__user-role"
              style={{ color: badge.color }}
            >
              {badge.label}
            </span>
          </div>
        </div>
        <button className="sidebar__logout" onClick={handleLogout} title="Cerrar sesión">
          <IconLogout />
        </button>
      </div>
    </aside>
  );
};
